import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Review Service - Handles all operations related to two-sided ratings and reviews
 */
const reviewService = {
  /**
   * Create a review
   * @param {string} reviewerId - Reviewer user ID
   * @param {string} reviewerType - Reviewer type ('brand' or 'influencer')
   * @param {string} revieweeId - Reviewee user ID
   * @param {string} revieweeType - Reviewee type ('brand' or 'influencer')
   * @param {string} campaignId - Campaign ID
   * @param {string} participantId - Campaign participant ID
   * @param {number} rating - Rating from 1 to 5
   * @param {string} reviewText - Review text content
   * @returns {Promise<object>} - Created review object with ID
   */
  createReview: async (reviewerId, reviewerType, revieweeId, revieweeType, campaignId, participantId, rating, reviewText) => {
    try {
      const existingReview = await reviewService.getReviewByCampaignAndReviewer(
        campaignId, 
        reviewerId, 
        participantId
      );
      
      if (existingReview) {
        throw new Error('A review already exists for this campaign and participant');
      }
      
      const reviewsRef = collection(db, 'reviews');
      
      const reviewData = {
        reviewerId,
        reviewerType,
        revieweeId,
        revieweeType,
        campaignId,
        participantId,
        rating,
        reviewText,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(reviewsRef, reviewData);
      
      return { id: docRef.id, ...reviewData };
    } catch (error) {
      console.error('Error creating review:', error);
      throw error;
    }
  },

  /**
   * Get review by ID
   * @param {string} reviewId - Review ID
   * @returns {Promise<object|null>} - Review data or null if not found
   */
  getReviewById: async (reviewId) => {
    try {
      const reviewRef = doc(db, 'reviews', reviewId);
      const reviewDoc = await getDoc(reviewRef);
      
      if (reviewDoc.exists()) {
        return { id: reviewDoc.id, ...reviewDoc.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting review:', error);
      throw error;
    }
  },

  /**
   * Get reviews for a reviewee (reviews received by a user)
   * @param {string} revieweeId - Reviewee user ID
   * @param {string} revieweeType - Reviewee type ('brand' or 'influencer')
   * @returns {Promise<Array>} - Array of review objects
   */
  getReviewsByReviewee: async (revieweeId, revieweeType) => {
    try {
      const reviewsRef = collection(db, 'reviews');
      const q = query(
        reviewsRef, 
        where('revieweeId', '==', revieweeId),
        where('revieweeType', '==', revieweeType),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      const reviews = [];
      querySnapshot.forEach((doc) => {
        reviews.push({ id: doc.id, ...doc.data() });
      });
      
      return reviews;
    } catch (error) {
      console.error('Error getting reviews by reviewee:', error);
      throw error;
    }
  },

  /**
   * Get reviews by a reviewer (reviews written by a user)
   * @param {string} reviewerId - Reviewer user ID
   * @returns {Promise<Array>} - Array of review objects
   */
  getReviewsByReviewer: async (reviewerId) => {
    try {
      const reviewsRef = collection(db, 'reviews');
      const q = query(
        reviewsRef, 
        where('reviewerId', '==', reviewerId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      const reviews = [];
      querySnapshot.forEach((doc) => {
        reviews.push({ id: doc.id, ...doc.data() });
      });
      
      return reviews;
    } catch (error) {
      console.error('Error getting reviews by reviewer:', error);
      throw error;
    }
  },

  /**
   * Get review by campaign, reviewer, and participant combination
   * @param {string} campaignId - Campaign ID
   * @param {string} reviewerId - Reviewer user ID
   * @param {string} participantId - Campaign participant ID
   * @returns {Promise<object|null>} - Review data or null if not found
   */
  getReviewByCampaignAndReviewer: async (campaignId, reviewerId, participantId) => {
    try {
      const reviewsRef = collection(db, 'reviews');
      const q = query(
        reviewsRef, 
        where('campaignId', '==', campaignId),
        where('reviewerId', '==', reviewerId),
        where('participantId', '==', participantId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting review by campaign and reviewer:', error);
      throw error;
    }
  },

  /**
   * Get all reviews for a campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Array>} - Array of review objects
   */
  getReviewsByCampaign: async (campaignId) => {
    try {
      const reviewsRef = collection(db, 'reviews');
      const q = query(
        reviewsRef, 
        where('campaignId', '==', campaignId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      const reviews = [];
      querySnapshot.forEach((doc) => {
        reviews.push({ id: doc.id, ...doc.data() });
      });
      
      return reviews;
    } catch (error) {
      console.error('Error getting reviews by campaign:', error);
      throw error;
    }
  },

  /**
   * Update a review
   * @param {string} reviewId - Review ID
   * @param {number} rating - New rating from 1 to 5
   * @param {string} reviewText - New review text content
   * @returns {Promise<object>} - Updated review object
   */
  updateReview: async (reviewId, rating, reviewText) => {
    try {
      const reviewRef = doc(db, 'reviews', reviewId);
      const reviewDoc = await getDoc(reviewRef);
      
      if (!reviewDoc.exists()) {
        throw new Error(`Review with ID ${reviewId} not found`);
      }
      
      const review = reviewDoc.data();
      
      const updateData = {
        rating,
        reviewText,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(reviewRef, updateData);
      
      return { 
        id: reviewId, 
        ...review, 
        ...updateData 
      };
    } catch (error) {
      console.error('Error updating review:', error);
      throw error;
    }
  },

  /**
   * Delete a review
   * @param {string} reviewId - Review ID
   * @returns {Promise} - Promise that resolves when review is deleted
   */
  deleteReview: async (reviewId) => {
    try {
      const reviewRef = doc(db, 'reviews', reviewId);
      await deleteDoc(reviewRef);
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  },

  /**
   * Get average rating and total review count for a user
   * @param {string} revieweeId - Reviewee user ID
   * @param {string} revieweeType - Reviewee type ('brand' or 'influencer')
   * @returns {Promise<object>} - Object containing averageRating and totalReviews
   */
  getAverageRating: async (revieweeId, revieweeType) => {
    try {
      const reviews = await reviewService.getReviewsByReviewee(revieweeId, revieweeType);
      
      if (reviews.length === 0) {
        return { averageRating: 0, totalReviews: 0 };
      }
      
      const totalRating = reviews.reduce((sum, review) => sum + (review.rating || 0), 0);
      const averageRating = totalRating / reviews.length;
      
      return { 
        averageRating, 
        totalReviews: reviews.length 
      };
    } catch (error) {
      console.error('Error getting average rating:', error);
      throw error;
    }
  }
};

export default reviewService;
